import React, { createContext, useContext, useState, ReactNode } from "react";
import { v4 as uuidv4 } from "uuid";
import type { ProgressUpdate } from "../types/api";
import type { ChatMessage, Source } from "../types/frontend";
import { MessageRoles } from "../types/frontend";
/**
 * Type definition for the chat context value
 * @interface ChatContextType
 * @property {ChatMessage[]} messages - Array of chat messages
 * @property {function} addMessage - Function to add a new message to the chat
 * @property {function} updateLastMessage - Function to update the last message in the chat
 * @property {function} clearMessages - Function to clear all chat messages
 * @property {string} sessionId - Unique identifier for the chat session (UUID v4)
 * @property {ProgressUpdate | null} progress - Progress state for the chat
 * @property {function} setProgress - Function to set the progress state for the chat
 */
interface ChatContextType {
  messages: ChatMessage[];
  addMessage: (message: ChatMessage) => ChatMessage;
  updateLastMessage: (
    message: string,
    options?: { isProgress?: boolean }
  ) => void;
  clearMessages: () => void;
  sessionId: string;
  setSessionId: (sessionId: string) => void;
  progressUpdate: ProgressUpdate | null;
  setProgressUpdate: (progressUpdate: ProgressUpdate | null) => void;
}

// Create context with undefined default value
const ChatContext = createContext<ChatContextType | undefined>(undefined);

/**
 * Props for the ChatProvider component
 * @interface ChatProviderProps
 * @property {ReactNode} children - Child components to be wrapped by the provider
 */
interface ChatProviderProps {
  children: ReactNode;
}

/**
 * Generates a UUID v4
 * @returns {string} A UUID v4
 */
const generateUUID = (): string => {
  return uuidv4();
};

/**
 * ChatProvider component
 * Provides chat state and management functions to child components
 * Manages messages array and provides functions to add/clear messages
 *
 * @component
 * @param {ChatProviderProps} props - Component props
 * @returns {React.ReactElement} Provider component
 */
export const ChatProvider: React.FC<ChatProviderProps> = ({ children }) => {
  // Initialize messages state
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  // Initialize sessionId with UUID v4 and expose setter
  const [sessionId, setSessionId] = useState<string>(generateUUID);
  // Initialize progress state
  const [progressUpdate, setProgressUpdate] = useState<ProgressUpdate | null>(
    null
  );

  /**
   * Adds a new message to the chat history, generating a traceId for user messages.
   * The traceId is used to link user messages with assistant responses in the tracing system.
   * This enables monitoring and debugging of chat interactions through LangFuse.
   *
   * @param {ChatMessage} message - Message to add
   * @returns {ChatMessage} The fully populated message with traceId if needed
   */
  const addMessage = (message: ChatMessage): ChatMessage => {
    if (message.role === MessageRoles.Assistant && !message.traceId) {
      throw new Error(
        `Trace ID is required for messages with role ${MessageRoles.Assistant}`
      );
    }
    // If the message is a user message, generate a new traceId
    // Otherwise, we assume the traceId is already set,
    // NOTE: I've decided to keep this in the context to keep the ID
    // generation logic in one place, although it feels a bit wonky to have
    // it here and not in the useChat hook
    if (message.role === MessageRoles.User) {
      message.traceId = generateUUID();
    }
    const newMessage = {
      ...message,
    };

    setMessages((prevMessages) => [...prevMessages, newMessage]);
    return newMessage;
  };

  /**
   * Updates the content of the last message in the chat
   * @param {string} message - New message content
   * @param {Object} options - Additional options for updating the message
   * @param {boolean} [options.isProgress] - Whether the message is a progress message
   */
  const updateLastMessage = (
    message: string,
    options?: { isProgress?: boolean },
    sources?: Source[]
  ) => {
    setMessages((prevMessages) => {
      if (prevMessages.length === 0) return prevMessages;
      const newMessages = [...prevMessages];
      newMessages[newMessages.length - 1] = {
        ...newMessages[newMessages.length - 1],
        content: message,
        ...(options?.isProgress !== undefined && {
          isProgress: options.isProgress,
        }),
        ...(sources !== undefined && {
          sources: sources,
        }),
      };
      return newMessages;
    });
  };

  /**
   * Clears all messages from the chat history and generates a new session ID
   */
  const clearMessages = () => {
    setMessages([]);
    setSessionId(generateUUID());
  };

  return (
    <ChatContext.Provider
      value={{
        messages,
        addMessage,
        updateLastMessage,
        clearMessages,
        sessionId,
        setSessionId,
        progressUpdate,
        setProgressUpdate,
      }}
    >
      {children}
    </ChatContext.Provider>
  );
};

/**
 * Custom hook to access the chat context
 * @throws {Error} If used outside of ChatProvider
 * @returns {ChatContextType} Chat context value
 */
export const useChatContext = () => {
  const context = useContext(ChatContext);
  if (context === undefined) {
    throw new Error("useChatContext must be used within a ChatProvider");
  }
  return context;
};
